import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, combineLatest, from, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  pluck,
  shareReplay,
  switchMap,
  take,
  throttleTime,
} from 'rxjs/operators';
import {
  CarrierScorecardEntry,
  CompanyDriverMetric,
  CompanyLoadsPerDayResponse,
  CompanyMetrics,
  CompanyRevenueResponse,
  CompanyShiftAverageResponse,
  CompanyShiftsPerDayResponse,
  DriverMetricDetail,
  DriverShiftOverviewRegion,
  DriverShiftOverviewResponse,
  LoHiOrderStatsRegion,
  LostEarningsByDriver,
  MetricCards,
  RevenueDay,
} from '../types';
import { AuthService, isAdmin } from './auth.service';
import { FeatureFlagService } from './feature-flags.service';

export enum KPIDuration {
  oneWeek = 7,
  twoWeeks = 14,
  oneMonth = 30,
  threeMonths = 90,
}

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private companyMetrics30Day$$ = new BehaviorSubject<CompanyMetrics>(null);
  private companyMetrics30DayThrottle$$ = new Subject();
  private companyMetrics30DayShared$ = this.companyMetrics30Day$$.pipe(shareReplay(1));
  public get companyMetrics30Day$(): Observable<CompanyMetrics> {
    this.companyMetrics30DayThrottle$$.next();
    return this.companyMetrics30DayShared$;
  }

  private companyDriverMetrics$$ = new BehaviorSubject(null);
  private companyDriverMetricsThrottle$$ = new Subject();
  private companyDriverMetricsShared$ = this.companyDriverMetrics$$.pipe(shareReplay(1));
  public get companyDriverMetrics$(): Observable<CompanyDriverMetric> {
    this.companyDriverMetricsThrottle$$.next();
    return this.companyDriverMetricsShared$;
  }

  private companyRevenueEntries$$ = new BehaviorSubject<CompanyRevenueResponse>(null);
  private companyRevenueEntriesThrottle$$ = new Subject();
  private companyRevenueEntriesShared$ = this.companyRevenueEntries$$.pipe(shareReplay(1));
  public get companyRevenueEntries$(): Observable<CompanyRevenueResponse> {
    this.companyRevenueEntriesThrottle$$.next();
    return this.companyRevenueEntriesShared$;
  }

  private companyShiftAverages$$ = new BehaviorSubject<CompanyShiftAverageResponse>(null);
  private companyShiftAveragesThrottle$$ = new Subject();
  private companyShiftAveragesShared$ = this.companyShiftAverages$$.pipe(shareReplay(1));
  public get companyShiftAverages$(): Observable<CompanyShiftAverageResponse> {
    this.companyShiftAveragesThrottle$$.next();
    return this.companyShiftAveragesShared$;
  }

  private lostEarningsByDriver$$ = new BehaviorSubject<LostEarningsByDriver[]>(null);
  private lostEarningsByDriverThrottle$$ = new Subject();
  private lostEarningsByDriverShared$ = this.lostEarningsByDriver$$.pipe(shareReplay(1));
  public get lostEarningsByDriver$(): Observable<LostEarningsByDriver[]> {
    this.lostEarningsByDriverThrottle$$.next();
    return this.lostEarningsByDriverShared$;
  }

  private shiftPerDay$$ = new BehaviorSubject<CompanyShiftsPerDayResponse>(null);
  private shiftPerDayThrottle$$ = new Subject();
  private shiftPerDayShared$ = this.shiftPerDay$$.pipe(shareReplay(1));
  public get shiftPerDay$(): Observable<CompanyShiftsPerDayResponse> {
    this.shiftPerDayThrottle$$.next();
    return this.shiftPerDayShared$;
  }

  private loadsPerDay$$ = new BehaviorSubject<CompanyLoadsPerDayResponse>(null);
  private loadsPerDayThrottle$$ = new Subject();
  private loadsPerDayShared$ = this.loadsPerDay$$.pipe(shareReplay(1));
  public get loadsPerDay$(): Observable<CompanyLoadsPerDayResponse> {
    this.loadsPerDayThrottle$$.next();
    return this.loadsPerDayShared$;
  }

  private companyScorecard$$ = new BehaviorSubject<CarrierScorecardEntry[]>([]);
  private companyScorecardThrottle$$ = new Subject();
  private companyScorecardShared$ = this.companyScorecard$$.pipe(shareReplay(1));
  public get companyScorecard$(): Observable<CarrierScorecardEntry[]> {
    this.companyScorecardThrottle$$.next();
    return this.companyScorecardShared$;
  }

  private driversOnShiftV2$$ = new BehaviorSubject<DriverShiftOverviewResponse>(null);
  private driversOnShiftV2Throttle$$ = new Subject();
  private driversOnShiftV2Shared$ = this.driversOnShiftV2$$.pipe(shareReplay(1));
  public get driversOnShiftV2$(): Observable<DriverShiftOverviewResponse> {
    this.driversOnShiftV2Throttle$$.next();
    return this.driversOnShiftV2Shared$;
  }
  private regionNamesNotMeetingCommitmentShared$: Observable<string[]> = this.driversOnShiftV2$.pipe(
    map((response) => regionsNotMeetingCommitment(response?.regions || [])),
    shareReplay(1),
  );

  public get regionNamesNotMeetingCommitment$(): Observable<string[]> {
    this.driversOnShiftV2Throttle$$.next();
    return this.regionNamesNotMeetingCommitmentShared$;
  }

  private thisWeekShiftCountByRegion$$ = new BehaviorSubject<Record<number, Record<string, number>>>(null);
  private thisWeekShiftCountByRegionThrottle$$ = new Subject();
  private thisWeekShiftCountByRegionShared$ = this.thisWeekShiftCountByRegion$$.pipe(shareReplay(1));
  public get thisWeekShiftCountByRegion$(): Observable<Record<number, Record<string, number>>> {
    this.thisWeekShiftCountByRegionThrottle$$.next();
    return this.thisWeekShiftCountByRegionShared$;
  }

  private regionalOrderStats$$ = new BehaviorSubject<LoHiOrderStatsRegion[]>(null);
  private regionalOrderStatsThrottle$$ = new Subject();
  private regionalOrderStatsShared$ = this.regionalOrderStats$$.pipe(shareReplay(1));
  public get regionalOrderStats$(): Observable<LoHiOrderStatsRegion[]> {
    this.regionalOrderStatsThrottle$$.next();
    return this.regionalOrderStatsShared$;
  }

  private duration$$ = new BehaviorSubject<KPIDuration>(KPIDuration.oneMonth);
  public get duration$() {
    return this.duration$$.asObservable();
  }

  private revenueData$$ = new BehaviorSubject<{
    days: RevenueDay[];
    metricCards: MetricCards;
  }>({
    days: [],
    metricCards: {
      totalRevenue: { currentValueCents: 0, previousValueCents: 0 },
      averageRevenuePerShift: { currentValueCents: 0, previousValueCents: 0 },
      averageMissedRevenuePerShift: { currentValueCents: 0, previousValueCents: 0 },
      averageRevenuePerWeek: { currentValueCents: 0, previousValueCents: 0 },
      averageTurnRatePerShift: { currentValue: 0, previousValue: 0 },
    },
  });

  // Observable for external use
  public revenueData$ = this.revenueData$$.asObservable();

  // Combine companyId and duration streams
  private companyDuration$ = combineLatest([
    this.authService.userInfo$.pipe(map((userInfo) => userInfo?.companyId)),
    this.duration$,
  ]);

  constructor(
    private httpClient: HttpClient,
    private logger: NGXLogger,
    private featureFlagService: FeatureFlagService,
    private authService: AuthService,
  ) {
    this.companyScorecardThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadCompanyScorecard();
    });

    this.driversOnShiftV2Throttle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriversOnShift();
    });

    this.companyMetrics30DayThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.load30DayCompanyMetrics();
    });

    this.companyDriverMetricsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadCompanyDriverMetrics();
    });

    this.companyRevenueEntriesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.load30DayCompanyRevenueEntries();
    });

    this.companyShiftAveragesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.load30DayCompanyShiftAverages();
    });

    this.lostEarningsByDriverThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.load30DayCompanyLostRevenuePerDriver();
    });

    this.shiftPerDayThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.load30DayCompanyShiftsPerDay();
    });

    this.loadsPerDayThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.load30DayCompanyLoadsPerDay();
    });

    this.thisWeekShiftCountByRegionThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadThisWeekShiftCountByRegion();
    });

    this.regionalOrderStatsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadRegionalStats();
    });

    this.companyDuration$
      .pipe(
        distinctUntilChanged(),
        switchMap(([companyId, duration]) => from(this.loadRevenueData(duration))),
        catchError((err) => {
          console.error('Error fetching revenue data:', err);

          return of({
            days: [],
            metricCards: {
              totalRevenue: { currentValueCents: 0, previousValueCents: 0 },
              averageRevenuePerShift: { currentValueCents: 0, previousValueCents: 0 },
              averageMissedRevenuePerShift: { currentValueCents: 0, previousValueCents: 0 },
              averageRevenuePerWeek: { currentValueCents: 0, previousValueCents: 0 },
              averageTurnRatePerShift: { currentValue: 0, previousValue: 0 },
            },
          });
        }),
      )
      .subscribe((data) => {
        this.revenueData$$.next(data);
      });
  }

  private async loadCompanyScorecard() {
    try {
      const result = await this.httpClient
        .get<CarrierScorecardEntry[]>(`${environment.api}/v1/admin/company_scorecard`)
        .toPromise();
      this.companyScorecard$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadDriversOnShift() {
    let version = '2';
    if (await this.featureFlagService.isFlagActive$('shiftChartV3').pipe(take(1)).toPromise()) {
      version = '3';
    }
    if (!isAdmin(await this.authService.userInfo$.pipe(take(1)).toPromise())) {
      return;
    }
    try {
      const result = await this.httpClient
        .get<DriverShiftOverviewResponse>(`${environment.api}/v1/admin/company_driver_shifts_overview`, {
          params: {
            version,
          },
        })
        .toPromise();
      this.driversOnShiftV2$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async load30DayCompanyMetrics() {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      const superhighway = await this.featureFlagService.isFlagActive$('superhighway').pipe(take(1)).toPromise();
      const result = await this.httpClient
        .get<CompanyMetrics>(
          `${environment.api}/${earningsProgram && superhighway ? 'v2' : 'v1'}/admin/company_earnings_summary_30_day`,
        )
        .toPromise();
      this.companyMetrics30Day$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadCompanyDriverMetrics() {
    try {
      const result = await this.httpClient
        .get<CompanyDriverMetric>(`${environment.api}/v2/admin/driver_current_tier_metric`)
        .toPromise();
      this.companyDriverMetrics$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async getDriverMetricDetails(driverID: string): Promise<DriverMetricDetail> {
    try {
      return await this.httpClient
        .get<DriverMetricDetail>(`${environment.api}/v2/admin/driver_current_tier_metric/detail/${driverID}`)
        .toPromise();
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async load30DayCompanyRevenueEntries() {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      const superhighway = await this.featureFlagService.isFlagActive$('superhighway').pipe(take(1)).toPromise();
      const result = await this.httpClient
        .get<CompanyRevenueResponse>(
          `${environment.api}/${earningsProgram && superhighway ? 'v2' : 'v1'}/admin/company_revenue_entries_30_day`,
        )
        .toPromise();
      this.companyRevenueEntries$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async load30DayCompanyShiftAverages() {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      const superhighway = await this.featureFlagService.isFlagActive$('superhighway').pipe(take(1)).toPromise();
      const result = await this.httpClient
        .get<CompanyShiftAverageResponse>(
          `${environment.api}/${earningsProgram && superhighway ? 'v2' : 'v1'}/admin/company_revenue_per_shift_30_day`,
        )
        .toPromise();
      this.companyShiftAverages$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async load30DayCompanyLostRevenuePerDriver() {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      const superhighway = await this.featureFlagService.isFlagActive$('superhighway').pipe(take(1)).toPromise();
      const result = await this.httpClient
        .get<{ entries: LostEarningsByDriver[] }>(
          `${environment.api}/${
            earningsProgram && superhighway ? 'v2' : 'v1'
          }/admin/company_lost_earnings_per_driver_30_day`,
        )
        .toPromise();
      this.lostEarningsByDriver$$.next(result.entries);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async load30DayCompanyShiftsPerDay() {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      const superhighway = await this.featureFlagService.isFlagActive$('superhighway').pipe(take(1)).toPromise();
      const result = await this.httpClient
        .get<CompanyShiftsPerDayResponse>(
          `${environment.api}/${earningsProgram && superhighway ? 'v2' : 'v1'}/admin/company_shifts_per_day`,
        )
        .toPromise();
      this.shiftPerDay$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async load30DayCompanyLoadsPerDay() {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      const superhighway = await this.featureFlagService.isFlagActive$('superhighway').pipe(take(1)).toPromise();
      const result = await this.httpClient
        .get<CompanyLoadsPerDayResponse>(
          `${environment.api}/${earningsProgram && superhighway ? 'v2' : 'v1'}/admin/company_loads_per_day`,
        )
        .toPromise();
      this.loadsPerDay$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadThisWeekShiftCountByRegion() {
    try {
      const result = await this.httpClient
        .get<CompanyLoadsPerDayResponse>(`${environment.api}/v1/admin/company_loads_per_day`)
        .toPromise();
      this.loadsPerDay$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadRegionalStats() {
    try {
      const result = await this.httpClient
        .get<{ regionalOrderStats: LoHiOrderStatsRegion[] }>(
          `${environment.api}/v1/admin/regional_stats_with_qualified_info`,
        )
        .toPromise();
      this.regionalOrderStats$$.next(result.regionalOrderStats);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadRevenueData(duration: KPIDuration): Promise<{ days: RevenueDay[]; metricCards: MetricCards }> {
    try {
      const result = await this.httpClient
        .get<{ days: RevenueDay[]; metricCards: MetricCards }>(`${environment.api}/v2/admin/total_revenue_graph_data`, {
          params: { daysOfHistory: duration.toString() },
        })
        .toPromise();

      this.revenueData$$.next(result);
      return result;
    } catch (error) {
      this.logger.error('Error loading revenue data:', error);

      const fallbackData = {
        days: [],
        metricCards: {
          totalRevenue: { currentValueCents: 0, previousValueCents: 0 },
          averageRevenuePerShift: { currentValueCents: 0, previousValueCents: 0 },
          averageMissedRevenuePerShift: { currentValueCents: 0, previousValueCents: 0 },
          averageRevenuePerWeek: { currentValueCents: 0, previousValueCents: 0 },
          averageTurnRatePerShift: { currentValue: 0, previousValue: 0 },
        },
      };

      this.revenueData$$.next(fallbackData);
      return fallbackData;
    }
  }
}

function regionsNotMeetingCommitment(regions: DriverShiftOverviewRegion[]): string[] {
  if (!regions || !regions.length) {
    return [];
  }
  const regionsNamesNotMeetingCommitment: string[] = [];
  regions.forEach((region) => {
    let consecutiveMatches = 0;
    for (const entry of region.overviews.slice(0, 24)) {
      const totalShifts = entry.actualShiftCount + entry.scheduledShiftCount;
      const minTarget = entry.commitment;
      if (totalShifts < minTarget) {
        consecutiveMatches += 1;
      } else {
        // reset
        consecutiveMatches = 0;
      }
      if (consecutiveMatches >= 2) {
        regionsNamesNotMeetingCommitment.push(region.name);
        break;
      }
    }
  });
  return regionsNamesNotMeetingCommitment;
}
